import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { ChevronUp, ChevronDown, Download } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { exportCsv } from "@/shared/lib/exportCsv";
import { StatusBadge } from "../StatusBadge";
import { submittedInvoices, type SubmittedInvoice } from "../../data/adminDashboard";

function formatAmount(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

export function SubmittedInvoicesTable({
  rows = submittedInvoices,
}: {
  rows?: SubmittedInvoice[];
}) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const labels = t.safecheck.adminDashboard.submittedInvoices;
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.hospital.toLowerCase().includes(q) ||
        row.company.toLowerCase().includes(q) ||
        String(row.displayId).includes(q),
    );
  }, [search, rows]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageData = filtered.slice(start, start + pageSize);
  const from = filtered.length === 0 ? 0 : start + 1;
  const to = Math.min(start + pageSize, filtered.length);

  const columns = [
    labels.columns.id,
    labels.columns.hospital,
    labels.columns.company,
    labels.columns.amount,
    labels.columns.submittedDate,
    labels.columns.delay,
    labels.columns.priority,
    labels.columns.status,
    labels.columns.invoice,
  ];

  const handleDownload = (row: SubmittedInvoice) => {
    exportCsv(`facture-${row.displayId}.csv`, columns.slice(0, -1), [
      [
        String(row.displayId),
        row.hospital,
        row.company,
        formatAmount(row.amount),
        row.submittedDate,
        `${row.delayDays}j`,
        labels.urgent,
        labels.submitted,
      ],
    ]);
    showToast(t.actions.exported);
  };

  return (
    <div className="theme-card theme-card-static mt-5">
      <div className="border-b border-border px-5 py-4">
        <h3 className="text-sm font-semibold text-foreground">{labels.title}</h3>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted">
          <span>{labels.show}</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>{labels.entries}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted">{labels.search}</span>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="safecheck-table w-full min-w-[960px]">
          <thead>
            <tr className="border-b border-border bg-card-muted/50">
              {columns.map((header) => (
                <th
                  key={header}
                  className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                >
                  <span className="flex items-center gap-1">
                    {header}
                    <span className="flex flex-col">
                      <ChevronUp size={10} className="-mb-1" />
                      <ChevronDown size={10} />
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-muted">
                  —
                </td>
              </tr>
            ) : (
              pageData.map((row, index) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/dashboard/invoices/${row.displayId}`)}
                  className={`cursor-pointer border-b border-border last:border-0 hover:bg-primary-soft/30 ${
                    index % 2 === 0 ? "bg-card" : "bg-card-muted/30"
                  }`}
                >
                  <td className="px-4 py-3 text-sm font-medium text-foreground">#{row.displayId}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{row.hospital}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{row.company}</td>
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    {formatAmount(row.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{row.submittedDate}</td>
                  <td className="px-4 py-3 text-sm font-bold text-pink">{row.delayDays}j</td>
                  <td className="px-4 py-3">
                    <StatusBadge variant="danger">{labels.urgent}</StatusBadge>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge variant="info">{labels.submitted}</StatusBadge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      aria-label={labels.download}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDownload(row);
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-primary transition-colors hover:bg-accent/20"
                    >
                      <Download size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
        <p className="text-sm text-muted">
          {labels.pagination
            .replace("{from}", String(from))
            .replace("{to}", String(to))
            .replace("{total}", String(filtered.length))}
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
          >
            {labels.previous}
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
            <button
              key={pageNum}
              type="button"
              onClick={() => setPage(pageNum)}
              className={`min-w-9 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                pageNum === currentPage
                  ? "bg-primary text-white"
                  : "border border-border text-muted-foreground hover:bg-accent/20 hover:border-accent"
              }`}
            >
              {pageNum}
            </button>
          ))}
          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
          >
            {labels.next}
          </button>
        </div>
      </div>
    </div>
  );
}
